/**
 * Statistics Hook - Phase 1
 * 
 * React hook for fetching and managing statistics data
 * Provides loading states, error handling, and data refetching
 */

import { useState, useEffect, useCallback } from 'react';
import { statisticsService } from '@/services/statisticsService';
import { 
  DashboardStats, 
  TimePeriod, 
  CustomPeriod,
  LoadingState,
  StatisticsError
} from '@/types/statistics';

interface UseStatisticsResult {
  data: DashboardStats | null;
  loading: LoadingState;
  error: StatisticsError | null;
  refetch: () => Promise<void>;
  isInitialLoad: boolean;
}

/**
 * Main hook for fetching complete dashboard statistics
 */
export const useStatistics = (
  uid: string, 
  period: TimePeriod = 'month',
  customPeriod?: CustomPeriod
): UseStatisticsResult => {
  
  const [data, setData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState<LoadingState>({
    overall: true,
    letters: true,
    trends: true,
    fingers: true,
    weakSpots: true,
    calendar: true
  });
  const [error, setError] = useState<StatisticsError | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const fetchStats = useCallback(async () => {
    if (!uid) {
      setError({
        code: 'INVALID_PERIOD',
        message: 'User ID is required'
      });
      return;
    }
    
    try {
      console.log('🔄 Fetching statistics for:', { uid, period });
      
      setError(null);
      setLoading({
        overall: true,
        letters: true,
        trends: true,
        fingers: true,
        weakSpots: true,
        calendar: true
      });
      
      const stats = await statisticsService.getDashboardStats(uid, period, customPeriod);
      
      console.log('✅ Statistics loaded successfully');
      setData(stats);
      
    } catch (err) {
      console.error('❌ Failed to load statistics:', err);
      
      setError({
        code: 'FETCH_ERROR',
        message: err instanceof Error ? err.message : 'Failed to load statistics',
        details: err
      });
    } finally {
      setLoading({
        overall: false,
        letters: false,
        trends: false,
        fingers: false,
        weakSpots: false,
        calendar: false
      });
      setIsInitialLoad(false);
    }
  }, [uid, period, customPeriod]);
  
  // Initial load and reload when dependencies change
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  
  return {
    data,
    loading,
    error,
    refetch: fetchStats,
    isInitialLoad
  };
};

/**
 * Hook for fetching specific widget data with granular loading states
 */
export const useStatisticsWidget = <T extends keyof DashboardStats>(
  uid: string,
  widget: T,
  period: TimePeriod = 'month'
) => {
  const [data, setData] = useState<DashboardStats[T] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<StatisticsError | null>(null);
  
  const fetchWidgetData = useCallback(async () => {
    if (!uid) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const fullStats = await statisticsService.getDashboardStats(uid, period);
      setData(fullStats[widget]);
      
    } catch (err) {
      setError({
        code: 'FETCH_ERROR',
        message: `Failed to load ${widget} data`,
        details: err
      });
    } finally {
      setLoading(false);
    }
  }, [uid, widget, period]);
  
  useEffect(() => {
    fetchWidgetData();
  }, [fetchWidgetData]);
  
  return { data, loading, error, refetch: fetchWidgetData };
};

/**
 * Hook for time period management with validation
 */
export const useTimePeriod = (initialPeriod: TimePeriod = 'month') => {
  const [period, setPeriod] = useState<TimePeriod>(initialPeriod);
  const [customPeriod, setCustomPeriod] = useState<CustomPeriod | undefined>();
  
  const updatePeriod = useCallback((newPeriod: TimePeriod, custom?: CustomPeriod) => {
    setPeriod(newPeriod);
    
    if (newPeriod === 'custom') {
      if (!custom) {
        throw new Error('Custom period is required when period is "custom"');
      }
      setCustomPeriod(custom);
    } else {
      setCustomPeriod(undefined);
    }
  }, []);
  
  const isValidPeriod = useCallback((period: TimePeriod, custom?: CustomPeriod): boolean => {
    if (period === 'custom') {
      return !!custom && custom.startDate <= custom.endDate;
    }
    return ['all', 'month', 'week'].includes(period);
  }, []);
  
  return {
    period,
    customPeriod,
    updatePeriod,
    isValidPeriod
  };
};

/**
 * Hook for managing filter and sort options
 */
export const useStatisticsFilters = () => {
  const [sortBy, setSortBy] = useState<'accuracy' | 'speed' | 'practiceCount' | 'trend'>('practiceCount');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'chart' | 'table' | 'grid'>('chart');
  const [showOnlyProblematic, setShowOnlyProblematic] = useState(false);
  
  const toggleSort = useCallback((field: typeof sortBy) => {
    if (sortBy === field) {
      setSortDirection(current => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  }, [sortBy]);
  
  return {
    sortBy,
    sortDirection,
    viewMode,
    showOnlyProblematic,
    setSortBy,
    setSortDirection,
    setViewMode,
    setShowOnlyProblematic,
    toggleSort
  };
};

/**
 * Hook for achievement progress tracking
 */
export const useAchievements = (uid: string) => {
  const { data } = useStatistics(uid, 'all');
  const achievements = data?.achievements || [];
  
  const achievedCount = achievements.filter(a => a.achieved).length;
  const totalCount = achievements.length;
  const progressPercentage = totalCount > 0 ? (achievedCount / totalCount) * 100 : 0;
  
  const recentAchievements = achievements
    .filter(a => a.achieved && a.unlockedDate)
    .sort((a, b) => {
      if (!a.unlockedDate || !b.unlockedDate) return 0;
      return b.unlockedDate.getTime() - a.unlockedDate.getTime();
    })
    .slice(0, 3);
  
  const nextAchievements = achievements
    .filter(a => !a.achieved)
    .sort((a, b) => b.progress - a.progress)
    .slice(0, 3);
  
  return {
    achievements,
    achievedCount,
    totalCount,
    progressPercentage: Math.round(progressPercentage),
    recentAchievements,
    nextAchievements
  };
};

/**
 * Hook for performance trend analysis
 */
export const usePerformanceTrends = (uid: string, period: TimePeriod = 'month') => {
  const { data } = useStatistics(uid, period);
  
  if (!data) {
    return {
      isImproving: false,
      accuracyTrend: 'stable' as const,
      speedTrend: 'stable' as const,
      consistencyScore: 0,
      insights: []
    };
  }
  
  const { overall, dailyProgress } = data;
  
  // Calculate trends
  const accuracyTrend = overall.trend;
  const speedTrend = overall.improvementPercent > 5 ? 'up' : 
                    overall.improvementPercent < -5 ? 'down' : 'stable';
  
  // Calculate consistency (how many days with practice)
  const daysWithPractice = dailyProgress.filter(day => day.sessionsCount > 0).length;
  const totalDays = dailyProgress.length;
  const consistencyScore = totalDays > 0 ? Math.round((daysWithPractice / totalDays) * 100) : 0;
  
  // Generate insights
  const insights: string[] = [];
  
  if (overall.accuracy >= 95) {
    insights.push('Excellent accuracy! Focus on building speed.');
  } else if (overall.accuracy < 85) {
    insights.push('Focus on accuracy before increasing speed.');
  }
  
  if (consistencyScore >= 80) {
    insights.push('Great practice consistency!');
  } else if (consistencyScore < 50) {
    insights.push('Try to practice more regularly for better results.');
  }
  
  if (overall.improvementPercent > 10) {
    insights.push('Amazing progress! Keep up the great work.');
  }
  
  return {
    isImproving: overall.trend === 'up',
    accuracyTrend,
    speedTrend,
    consistencyScore,
    insights
  };
};

/**
 * Hook for export functionality (future enhancement)
 */
export const useStatisticsExport = (_uid: string) => {
  const [isExporting, setIsExporting] = useState(false);
  
  const exportToCSV = useCallback(async (_period: TimePeriod) => {
    setIsExporting(true);
    try {
      // Implementation for CSV export
      console.log('Exporting statistics to CSV...');
      // This would be implemented in Phase 2
    } finally {
      setIsExporting(false);
    }
  }, []);
  
  const exportToPDF = useCallback(async (_period: TimePeriod) => {
    setIsExporting(true);
    try {
      // Implementation for PDF export
      console.log('Exporting statistics to PDF...');
      // This would be implemented in Phase 2
    } finally {
      setIsExporting(false);
    }
  }, []);
  
  return {
    isExporting,
    exportToCSV,
    exportToPDF
  };
};
